import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Radio, 
  RadioGroup, 
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  Tooltip,
  Paper,
  Avatar,
  Grid,
  alpha
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import QuizIcon from '@mui/icons-material/Quiz';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import TuneIcon from '@mui/icons-material/Tune';
import QuestionEditDialog from '../QuestionEditDialog/QuestionEditDialog';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436',
  light: '#dfe6e9'
};

const MIN_QUESTIONS = 2;
const MAX_QUESTIONS = 50;

const DIFFICULTY_LEVELS = [
  { value: 'basico', label: 'Básico', color: COLORS.success },
  { value: 'intermedio', label: 'Intermedio', color: COLORS.warning },
  { value: 'avanzado', label: 'Avanzado', color: COLORS.error }
];

const TestCreator = ({ test = { questions: [], config: { questions_per_test: 10 } }, onChange }) => {
  const [questions, setQuestions] = useState(test.questions || []);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState('');
  const [questionDifficulty, setQuestionDifficulty] = useState('basico');
  const [testConfig, setTestConfig] = useState({
    questions_per_test: test.config?.questions_per_test || 10,
    rotateQuestions: test.config?.rotateQuestions || true,
    requireAllDifficulties: test.config?.requireAllDifficulties || false
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditQuestion, setCurrentEditQuestion] = useState(null);
  const [warning, setWarning] = useState('');

  const handleConfigChange = (field, value) => {
    if (field === 'questions_per_test') {
      if (value < MIN_QUESTIONS) {
        setError(`El número mínimo de preguntas es ${MIN_QUESTIONS}`);
        return;
      }
      if (value > MAX_QUESTIONS) {
        setError(`El número máximo de preguntas es ${MAX_QUESTIONS}`);
        return;
      }
    }
    
    setTestConfig(prev => ({ ...prev, [field]: value }));
    setError('');

    onChange({
      questions: questions,
      config: { ...testConfig, [field]: value }
    });
  };

  const handleAddQuestion = () => {
    setError('');
    if (questions.length >= MAX_QUESTIONS && editingIndex === null) {
      setError(`No se pueden añadir más de ${MAX_QUESTIONS} preguntas al pool.`);
      return;
    }
    if (currentQuestion && options.some(opt => opt) && correctAnswer) {
      const filteredOptions = options.filter(opt => opt !== '');
      const uniqueOptions = [...new Set(filteredOptions)];
      if (uniqueOptions.length !== filteredOptions.length) {
        setError('No se permiten opciones duplicadas.');
        return;
      }
      if (!filteredOptions.includes(correctAnswer)) {
        setError('La respuesta correcta debe ser una de las opciones no vacías.');
        return;
      }
      const newQuestion = {
        question: currentQuestion,
        options: filteredOptions,
        correctAnswer: correctAnswer,
        difficulty: questionDifficulty,
        isActive: true
      };
      if (editingIndex !== null) {
        const updatedQuestions = [...questions];
        updatedQuestions[editingIndex] = newQuestion;
        setQuestions(updatedQuestions);
        setEditingIndex(null);
        onChange({ questions: updatedQuestions, config: testConfig });
      } else {
        const newQuestions = [...questions, newQuestion];
        setQuestions(newQuestions);
        onChange({ questions: newQuestions, config: testConfig });
      }
      setCurrentQuestion('');
      setOptions(['', '', '', '']);
      setCorrectAnswer('');
    } else {
      setError('Por favor, completa todos los campos requeridos.');
    }
  };

  const handleRemoveQuestion = (index) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
    onChange({ questions: newQuestions, config: testConfig });
  };

  const handleEditQuestion = (index) => {
    setCurrentEditQuestion({...questions[index], index});
    setEditDialogOpen(true);
  };

  const countQuestionsByDifficulty = () => {
    return questions.reduce((acc, q) => {
      acc[q.difficulty || 'basico'] = (acc[q.difficulty || 'basico'] || 0) + 1;
      return acc;
    }, {});
  };

  const handleSaveQuestion = (editedQuestion) => {
    const newQuestions = [...questions];
    newQuestions[editedQuestion.index] = {
      question: editedQuestion.question,
      options: editedQuestion.options,
      correctAnswer: editedQuestion.correctAnswer,
      difficulty: editedQuestion.difficulty,
      isActive: true
    };
    setQuestions(newQuestions);
    onChange({ questions: newQuestions, config: testConfig });
    setEditDialogOpen(false);
  };

  const difficultyCount = countQuestionsByDifficulty();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(COLORS.purple, 0.3)}`
          }}
        >
          <QuizIcon sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
            Configuración del Test
          </Typography>
          <Typography variant="body2" sx={{ color: '#636e72' }}>
            Crea el banco de preguntas y configura el test final
          </Typography>
        </Box>
      </Box>

      {/* Configuración general */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '2px solid #e0e0e0',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <SettingsIcon sx={{ color: COLORS.primary }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark }}>
            Opciones Generales
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Preguntas por test"
              value={testConfig.questions_per_test}
              onChange={(e) => handleConfigChange('questions_per_test', parseInt(e.target.value))}
              inputProps={{ min: MIN_QUESTIONS, max: MAX_QUESTIONS }}
              helperText={`Mínimo ${MIN_QUESTIONS}, máximo ${MAX_QUESTIONS}`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: COLORS.primary },
                  '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(COLORS.info, 0.08),
                height: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={testConfig.rotateQuestions}
                    onChange={(e) => handleConfigChange('rotateQuestions', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.primary },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.primary }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ShuffleIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                    <Typography sx={{ fontWeight: 500 }}>Rotar preguntas</Typography>
                    <Tooltip title="Las preguntas se seleccionarán aleatoriamente del pool">
                      <InfoIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
                    </Tooltip>
                  </Box>
                }
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(COLORS.purple, 0.08),
                height: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={testConfig.requireAllDifficulties}
                    onChange={(e) => handleConfigChange('requireAllDifficulties', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.purple },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.purple }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TuneIcon sx={{ fontSize: 18, color: COLORS.purple }} />
                    <Typography sx={{ fontWeight: 500 }}>Todas las dificultades</Typography>
                    <Tooltip title="El test incluirá preguntas de todos los niveles disponibles">
                      <InfoIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
                    </Tooltip>
                  </Box>
                }
              />
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {warning && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>{warning}</Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Crear preguntas */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '2px solid #e0e0e0',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AddIcon sx={{ color: COLORS.success }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark }}>
              Crear Nueva Pregunta
            </Typography>
          </Box>
          
          {/* Contador por dificultad */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {DIFFICULTY_LEVELS.map(level => (
              <Chip
                key={level.value}
                label={`${level.label}: ${difficultyCount[level.value] || 0}`}
                size="small"
                sx={{
                  bgcolor: alpha(level.color, 0.15),
                  color: level.color,
                  fontWeight: 600
                }}
              />
            ))}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Dificultad</InputLabel>
              <Select
                value={questionDifficulty}
                onChange={(e) => setQuestionDifficulty(e.target.value)}
                label="Dificultad"
                sx={{ borderRadius: 2 }}
              >
                {DIFFICULTY_LEVELS.map(level => (
                  <MenuItem key={level.value} value={level.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: level.color }} />
                      {level.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Pregunta"
              value={currentQuestion}
              onChange={(e) => setCurrentQuestion(e.target.value)}
              placeholder="Escribe la pregunta aquí..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: COLORS.primary },
                  '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                }
              }}
            />
          </Grid>
        </Grid>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {options.map((option, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <TextField
                fullWidth
                label={`Opción ${index + 1}`}
                value={option}
                onChange={(e) => {
                  const newOptions = [...options];
                  newOptions[index] = e.target.value;
                  setOptions(newOptions);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: correctAnswer === option && option ? alpha(COLORS.success, 0.08) : 'transparent',
                    '&:hover fieldset': { borderColor: COLORS.primary },
                    '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.dark, mb: 1 }}>
            Selecciona la respuesta correcta:
          </Typography>
          <RadioGroup
            value={correctAnswer}
            onChange={(e) => setCorrectAnswer(e.target.value)}
            sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}
          >
            {options.map((option, index) => (
              option && (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    border: `2px solid ${correctAnswer === option ? COLORS.success : '#e0e0e0'}`,
                    bgcolor: correctAnswer === option ? alpha(COLORS.success, 0.08) : 'white',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FormControlLabel
                    value={option}
                    control={
                      <Radio 
                        sx={{
                          color: '#bdbdbd',
                          '&.Mui-checked': { color: COLORS.success }
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: correctAnswer === option ? 600 : 400 }}>
                        {option}
                      </Typography>
                    }
                  />
                </Paper>
              )
            ))}
          </RadioGroup>
        </Box>
        
        <Button 
          onClick={handleAddQuestion} 
          variant="contained"
          startIcon={editingIndex !== null ? <CheckCircleIcon /> : <AddIcon />}
          sx={{ 
            mt: 3,
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`,
            '&:hover': {
              boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}`
            }
          }}
        >
          {editingIndex !== null ? 'Actualizar Pregunta' : 'Añadir Pregunta'}
        </Button>
      </Paper>

      {/* Pool de preguntas */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '2px solid #e0e0e0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <QuizIcon sx={{ color: COLORS.purple }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark }}>
              Pool de Preguntas
            </Typography>
          </Box>
          <Chip
            label={`${questions.length}/${MAX_QUESTIONS}`}
            sx={{
              bgcolor: alpha(COLORS.purple, 0.15),
              color: COLORS.purple,
              fontWeight: 700
            }}
          />
        </Box>
        
        {questions.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
              border: '2px dashed #e0e0e0',
              bgcolor: alpha(COLORS.primary, 0.02)
            }}
          >
            <QuizIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
            <Typography sx={{ color: '#636e72' }}>
              No hay preguntas todavía. Añade tu primera pregunta arriba.
            </Typography>
          </Paper>
        ) : (
          <List sx={{ p: 0 }}>
            {questions.map((q, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{ 
                  mb: 1.5,
                  borderRadius: 2,
                  border: '2px solid #e0e0e0',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(COLORS.primary, 0.3),
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.1)}`
                  }
                }}
              >
                <ListItem sx={{ py: 2 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: alpha(COLORS.primary, 0.15),
                      color: COLORS.primary,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      mr: 2
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, color: COLORS.dark }}>
                          {q.question}
                        </Typography>
                        <Chip 
                          label={DIFFICULTY_LEVELS.find(l => l.value === q.difficulty)?.label || 'Básico'}
                          size="small"
                          sx={{
                            bgcolor: alpha(DIFFICULTY_LEVELS.find(l => l.value === q.difficulty)?.color || COLORS.success, 0.15),
                            color: DIFFICULTY_LEVELS.find(l => l.value === q.difficulty)?.color || COLORS.success,
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ color: '#636e72' }}>
                          Opciones: {q.options.join(' • ')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.success }} />
                          <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600 }}>
                            {q.correctAnswer}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Editar">
                      <IconButton 
                        onClick={() => handleEditQuestion(index)}
                        sx={{ color: COLORS.warning }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton 
                        onClick={() => handleRemoveQuestion(index)}
                        sx={{ color: COLORS.error }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              </Paper>
            ))}
          </List>
        )}
      </Paper>

      <QuestionEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        question={currentEditQuestion}
        onSave={handleSaveQuestion}
      />
    </Box>
  );
};

export default TestCreator;